import React, { useState, useEffect, useCallback } from 'react';
import { http } from '../../lib/api';
import { descargarPdfOrden } from '../../lib/download';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, Legend, AreaChart, Area,
} from 'recharts';
import {
  Clock, Users, UserCheck, AlertTriangle, TrendingUp,
  ChevronLeft, ChevronRight, ArrowLeft,
  Building, Timer, CalendarDays, ChevronDown,
  Loader2, Search, Eye, Download, RefreshCw,
  AlertCircle, CheckCircle2, X, FileText, FileSpreadsheet,
  Check, Printer,
} from 'lucide-react';

const MESES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function ControlHoras() {
  // --- Estado principal ---
  const [vista, setVista] = useState('clientes'); // clientes | tecnicos | detalle_cliente | detalle_tecnico
  const [periodo, setPeriodo] = useState('mes');
  const now = new Date();
  const [anio, setAnio] = useState(now.getFullYear());
  const [valor, setValor] = useState(periodo === 'semana' ? getISOWeek(now) : now.getMonth() + 1);

  // --- Datos ---
  const [resumenClientes, setResumenClientes] = useState(null);
  const [resumenTecnicos, setResumenTecnicos] = useState(null);
  const [detalleData, setDetalleData] = useState(null);
  const [historialData, setHistorialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedNombre, setSelectedNombre] = useState('');

  // Preview & Export modals
  const [previewOrden, setPreviewOrden] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Historial período
  const [histPeriodo, setHistPeriodo] = useState('mes');
  const [histCantidad, setHistCantidad] = useState(12);

  function getISOWeek(d) {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  }

  // --- Navegación de período ---
  const avanzarPeriodo = () => {
    if (periodo === 'mes') {
      if (valor === 12) { setValor(1); setAnio(a => a + 1); }
      else setValor(v => v + 1);
    } else if (periodo === 'semana') {
      if (valor >= 52) { setValor(1); setAnio(a => a + 1); }
      else setValor(v => v + 1);
    } else {
      setAnio(a => a + 1);
    }
  };

  const retrocederPeriodo = () => {
    if (periodo === 'mes') {
      if (valor === 1) { setValor(12); setAnio(a => a - 1); }
      else setValor(v => v - 1);
    } else if (periodo === 'semana') {
      if (valor <= 1) { setValor(52); setAnio(a => a - 1); }
      else setValor(v => v - 1);
    } else {
      setAnio(a => a - 1);
    }
  };

  const periodoLabel = useCallback(() => {
    if (periodo === 'mes') return `${MESES[valor]} ${anio}`;
    if (periodo === 'semana') return `Semana ${valor}, ${anio}`;
    return `${anio}`;
  }, [periodo, valor, anio]);

  // Al cambiar tipo de período, resetear valor
  useEffect(() => {
    const now = new Date();
    if (periodo === 'mes') setValor(now.getMonth() + 1);
    else if (periodo === 'semana') setValor(getISOWeek(now));
    else setValor(now.getFullYear());
    setAnio(now.getFullYear());
  }, [periodo]);

  // --- Cargar datos según vista ---
  const cargarDatos = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (vista === 'clientes') {
        const res = await http.get(`/control-horas/resumen-clientes?periodo=${periodo}&anio=${anio}&valor=${valor}`, { headers });
        setResumenClientes(res.data);
      } else if (vista === 'tecnicos') {
        const res = await http.get(`/control-horas/resumen-tecnicos?periodo=${periodo}&anio=${anio}&valor=${valor}`, { headers });
        setResumenTecnicos(res.data);
      } else if (vista === 'detalle_cliente' && selectedId) {
        const [det, hist] = await Promise.all([
          http.get(`/control-horas/detalle-cliente/${selectedId}?periodo=${periodo}&anio=${anio}&valor=${valor}`, { headers }),
          http.get(`/control-horas/historial?tipo=cliente&id=${selectedId}&periodo=${histPeriodo}&cantidad=${histCantidad}`, { headers }),
        ]);
        setDetalleData(det.data);
        setHistorialData(hist.data);
      } else if (vista === 'detalle_tecnico' && selectedId) {
        const [det, hist] = await Promise.all([
          http.get(`/control-horas/detalle-tecnico/${selectedId}?periodo=${periodo}&anio=${anio}&valor=${valor}`, { headers }),
          http.get(`/control-horas/historial?tipo=tecnico&id=${selectedId}&periodo=${histPeriodo}&cantidad=${histCantidad}`, { headers }),
        ]);
        setDetalleData(det.data);
        setHistorialData(hist.data);
      }
    } catch (err) {
      console.error('Error cargando datos de control de horas:', err);
    }
    setLoading(false);
  }, [vista, periodo, anio, valor, selectedId, histPeriodo, histCantidad]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // --- Helpers de UI ---
  const estadoColor = (estado) => {
    if (estado === 'excedido') return 'bg-red-100 text-red-700 border-red-200';
    if (estado === 'alerta') return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-green-100 text-green-700 border-green-200';
  };

  const estadoIcon = (estado) => {
    if (estado === 'excedido') return <AlertTriangle className="h-4 w-4" />;
    if (estado === 'alerta') return <AlertCircle className="h-4 w-4" />;
    return <CheckCircle2 className="h-4 w-4" />;
  };

  const progressColor = (pct) => {
    if (pct > 100) return 'bg-red-500';
    if (pct > 80) return 'bg-yellow-500';
    if (pct > 50) return 'bg-emerald-500';
    return 'bg-green-500';
  };

  const formatHoras = (h) => {
    if (h === null || h === undefined) return '0h';
    const hrs = Math.floor(h);
    const mins = Math.round((h - hrs) * 60);
    if (mins === 0) return `${hrs}h`;
    return `${hrs}h ${mins}m`;
  };

  const verDetalleCliente = (clienteId, nombre) => {
    setSelectedId(clienteId);
    setSelectedNombre(nombre);
    setVista('detalle_cliente');
  };

  const verDetalleTecnico = (tecnicoId, nombre) => {
    setSelectedId(tecnicoId);
    setSelectedNombre(nombre);
    setVista('detalle_tecnico');
  };

  const volverALista = () => {
    setSelectedId(null);
    setSelectedNombre('');
    setDetalleData(null);
    setHistorialData(null);
    if (vista === 'detalle_cliente') setVista('clientes');
    else setVista('tecnicos');
  };

  // --- Preview de orden ---
  const abrirPreviewOrden = async (ordenId) => {
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await http.get(`/ordenes/${ordenId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPreviewOrden(res.data);
    } catch (err) {
      console.error('Error cargando orden:', err);
      alert('No se pudo cargar la orden');
    }
    setPreviewLoading(false);
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            {(vista === 'detalle_cliente' || vista === 'detalle_tecnico') && (
              <button onClick={volverALista} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Timer className="h-6 w-6 text-emerald-600" />
                Control de Horas
              </h1>
              {(vista === 'detalle_cliente' || vista === 'detalle_tecnico') && (
                <p className="text-sm text-gray-500 mt-0.5">{selectedNombre}</p>
              )}
            </div>
          </div>

          {/* Tabs + Controles de Período */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {/* Tabs solo en vistas principales */}
            {vista !== 'detalle_cliente' && vista !== 'detalle_tecnico' && (
              <div className="flex bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setVista('clientes')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    vista === 'clientes' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <Building className="h-4 w-4 inline mr-1.5" />
                  Clientes
                </button>
                <button
                  onClick={() => setVista('tecnicos')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    vista === 'tecnicos' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  <UserCheck className="h-4 w-4 inline mr-1.5" />
                  Técnicos
                </button>
              </div>
            )}

            {/* Selector de período */}
            <div className="flex items-center gap-1.5">
              <select
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:ring-2 focus:ring-emerald-500"
              >
                <option value="semana">Semanal</option>
                <option value="mes">Mensual</option>
                <option value="anio">Anual</option>
              </select>

              <div className="flex items-center bg-white border border-gray-300 rounded-lg">
                <button onClick={retrocederPeriodo} className="p-1.5 hover:bg-gray-100 rounded-l-lg">
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-sm font-medium min-w-[120px] text-center">{periodoLabel()}</span>
                <button onClick={avanzarPeriodo} className="p-1.5 hover:bg-gray-100 rounded-r-lg">
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              <button
                onClick={cargarDatos}
                disabled={loading}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Actualizar"
              >
                <RefreshCw className={`h-4 w-4 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
              </button>

              <button
                onClick={() => setShowExportModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
                title="Exportar datos"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Exportar</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-500">Cargando datos...</span>
          </div>
        )}

        {/* VISTA: RESUMEN CLIENTES */}
        {!loading && vista === 'clientes' && resumenClientes && (
          <ResumenClientesView
            data={resumenClientes}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            formatHoras={formatHoras}
            estadoColor={estadoColor}
            estadoIcon={estadoIcon}
            progressColor={progressColor}
            onVerDetalle={verDetalleCliente}
          />
        )}

        {/* VISTA: RESUMEN TÉCNICOS */}
        {!loading && vista === 'tecnicos' && resumenTecnicos && (
          <ResumenTecnicosView
            data={resumenTecnicos}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            formatHoras={formatHoras}
            onVerDetalle={verDetalleTecnico}
          />
        )}

        {/* VISTA: DETALLE CLIENTE */}
        {!loading && vista === 'detalle_cliente' && detalleData && (
          <DetalleClienteView
            data={detalleData}
            historial={historialData}
            formatHoras={formatHoras}
            progressColor={progressColor}
            histPeriodo={histPeriodo}
            setHistPeriodo={setHistPeriodo}
            histCantidad={histCantidad}
            setHistCantidad={setHistCantidad}
            onPreviewOrden={abrirPreviewOrden}
          />
        )}

        {/* VISTA: DETALLE TÉCNICO */}
        {!loading && vista === 'detalle_tecnico' && detalleData && (
          <DetalleTecnicoView
            data={detalleData}
            historial={historialData}
            formatHoras={formatHoras}
            histPeriodo={histPeriodo}
            setHistPeriodo={setHistPeriodo}
            histCantidad={histCantidad}
            setHistCantidad={setHistCantidad}
            onPreviewOrden={abrirPreviewOrden}
          />
        )}
      </div>

      {/* MODAL: Preview de Orden */}
      {(previewOrden || previewLoading) && (
        <OrdenPreviewModal
          orden={previewOrden}
          loading={previewLoading}
          onClose={() => setPreviewOrden(null)}
          formatHoras={formatHoras}
        />
      )}

      {/* MODAL: Exportar */}
      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          periodo={periodo}
          anio={anio}
          valor={valor}
          vista={vista}
          selectedId={selectedId}
          selectedNombre={selectedNombre}
        />
      )}
    </div>
  );
}


// ============================================================
// SUB-COMPONENTE: RESUMEN CLIENTES
// ============================================================
function ResumenClientesView({ data, searchTerm, setSearchTerm, formatHoras, estadoColor, estadoIcon, progressColor, onVerDetalle }) {
  const { totales, clientes } = data;

  const clientesFiltrados = clientes.filter(c =>
    c.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* KPIs globales */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={<Clock className="h-5 w-5 text-emerald-600" />} label="Horas Contratadas" value={formatHoras(totales.horas_contratadas)} color="blue" />
        <KpiCard icon={<Timer className="h-5 w-5 text-green-600" />} label="Horas Trabajadas" value={formatHoras(totales.horas_trabajadas)} color="green" />
        <KpiCard icon={<TrendingUp className="h-5 w-5 text-purple-600" />} label="Utilización" value={`${totales.porcentaje_global}%`} color="purple" />
        <KpiCard icon={<AlertTriangle className="h-5 w-5 text-red-600" />} label="Clientes Excedidos" value={totales.clientes_excedidos} color="red" />
        <KpiCard icon={<AlertCircle className="h-5 w-5 text-yellow-600" />} label="En Alerta" value={totales.clientes_alerta} color="yellow" />
      </div>

      {/* Buscador */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
      </div>

      {/* Tabla de clientes */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Cliente</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Contratadas</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Trabajadas</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Restantes</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Excedidas</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700 hidden lg:table-cell">H. Extras</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Uso</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Estado</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              {clientesFiltrados.map((c, i) => (
                <tr key={c.cliente_id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{c.nombre}</div>
                    <div className="text-xs text-gray-500">{c.ordenes_count} órdenes</div>
                  </td>
                  <td className="text-center px-3 py-3 font-mono text-sm">{formatHoras(c.horas_contratadas)}</td>
                  <td className="text-center px-3 py-3 font-mono text-sm font-semibold" title={`Real: ${formatHoras(c.horas_trabajadas)}`}>{formatHoras(c.horas_computadas ?? c.horas_trabajadas)}</td>
                  <td className="text-center px-3 py-3 font-mono text-sm text-green-600">{formatHoras(c.horas_restantes)}</td>
                  <td className="text-center px-3 py-3 font-mono text-sm text-red-600 font-semibold">
                    {c.horas_excedidas > 0 ? formatHoras(c.horas_excedidas) : '-'}
                  </td>
                  <td className="text-center px-3 py-3 font-mono text-sm text-orange-600 hidden lg:table-cell">
                    {c.horas_extras_horario > 0 ? formatHoras(c.horas_extras_horario) : '-'}
                  </td>
                  <td className="text-center px-3 py-3">
                    <div className="flex items-center gap-2 justify-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${progressColor(c.porcentaje_uso)}`}
                          style={{ width: `${Math.min(100, c.porcentaje_uso)}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium w-10 text-right">{c.porcentaje_uso}%</span>
                    </div>
                  </td>
                  <td className="text-center px-3 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${estadoColor(c.estado)}`}>
                      {estadoIcon(c.estado)}
                      {c.estado === 'excedido' ? 'Excedido' : c.estado === 'alerta' ? 'Alerta' : 'OK'}
                    </span>
                  </td>
                  <td className="text-center px-3 py-3">
                    <button
                      onClick={() => onVerDetalle(c.cliente_id, c.nombre)}
                      className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="Ver detalle"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {clientesFiltrados.length === 0 && (
                <tr><td colSpan={9} className="text-center py-8 text-gray-500">No se encontraron clientes</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}


// ============================================================
// SUB-COMPONENTE: RESUMEN TÉCNICOS
// ============================================================
function ResumenTecnicosView({ data, searchTerm, setSearchTerm, formatHoras, onVerDetalle }) {
  const { totales, tecnicos } = data;

  const tecnicosFiltrados = tecnicos.filter(t =>
    t.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard icon={<Timer className="h-5 w-5 text-emerald-600" />} label="Horas Totales" value={formatHoras(totales.horas_totales)} color="blue" />
        <KpiCard icon={<Clock className="h-5 w-5 text-green-600" />} label="Horas Normales" value={formatHoras(totales.horas_normales)} color="green" />
        <KpiCard icon={<AlertTriangle className="h-5 w-5 text-orange-600" />} label="Horas Extras" value={formatHoras(totales.horas_extras)} color="orange" />
        <KpiCard icon={<Users className="h-5 w-5 text-purple-600" />} label="Técnicos Activos" value={totales.total_tecnicos} color="purple" />
      </div>

      {/* Gráfico de barras */}
      {tecnicos.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Distribución de Horas por Técnico</h3>
          <ResponsiveContainer width="100%" height={Math.max(200, tecnicos.length * 45)}>
            <BarChart data={tecnicos} layout="vertical" margin={{ left: 100, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis
                dataKey="nombre"
                type="category"
                tick={{ fontSize: 12 }}
                width={90}
              />
              <Tooltip
                formatter={(value, name) => [formatHoras(value), name === 'horas_normales' ? 'Normales' : 'Extras']}
                labelFormatter={(label) => label}
              />
              <Legend formatter={(value) => value === 'horas_normales' ? 'Normales' : 'Extras'} />
              <Bar dataKey="horas_normales" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
              <Bar dataKey="horas_extras" stackId="a" fill="#f97316" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Buscador */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar técnico..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
      </div>

      {/* Tabla de técnicos */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-700">Técnico</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Horas Totales</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Normales</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Extras</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700">Órdenes</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700 hidden md:table-cell">Clientes</th>
                <th className="text-center px-3 py-3 font-semibold text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              {tecnicosFiltrados.map((t, i) => (
                <tr key={t.tecnico_id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{t.nombre}</div>
                    <div className="text-xs text-gray-500">@{t.username}</div>
                  </td>
                  <td className="text-center px-3 py-3 font-mono text-sm font-semibold">{formatHoras(t.horas_totales)}</td>
                  <td className="text-center px-3 py-3 font-mono text-sm text-emerald-600">{formatHoras(t.horas_normales)}</td>
                  <td className="text-center px-3 py-3 font-mono text-sm font-semibold">
                    {t.horas_extras > 0 ? (
                      <span className="text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">{formatHoras(t.horas_extras)}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="text-center px-3 py-3 text-gray-700">{t.ordenes_count}</td>
                  <td className="text-center px-3 py-3 text-gray-700 hidden md:table-cell">{t.clientes_atendidos}</td>
                  <td className="text-center px-3 py-3">
                    <button
                      onClick={() => onVerDetalle(t.tecnico_id, t.nombre)}
                      className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="Ver detalle"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {tecnicosFiltrados.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">No se encontraron técnicos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}


// ============================================================
// SUB-COMPONENTE: DETALLE CLIENTE
// ============================================================
function DetalleClienteView({ data, historial, formatHoras, progressColor, histPeriodo, setHistPeriodo, histCantidad, setHistCantidad, onPreviewOrden }) {
  const { cliente, resumen, semanas, ordenes } = data;
  const [showOrdenes, setShowOrdenes] = useState(false);

  const porcentaje = resumen.porcentaje_uso;

  return (
    <>
      {/* Resumen del cliente */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">Contratadas ({data.periodo.label})</div>
          <div className="text-2xl font-bold text-gray-900">{formatHoras(cliente.horas_contratadas_periodo)}</div>
          <div className="text-xs text-gray-400 mt-1">{cliente.horas_contratadas_mes}h / mes</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">Computadas</div>
          <div className="text-2xl font-bold text-emerald-600">{formatHoras(resumen.horas_computadas ?? resumen.horas_trabajadas)}</div>
          <div className="text-xs text-gray-400 mt-1">{resumen.total_ordenes} órdenes</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">{resumen.horas_excedidas > 0 ? 'Excedidas' : 'Restantes'}</div>
          <div className={`text-2xl font-bold ${resumen.horas_excedidas > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {formatHoras(resumen.horas_excedidas > 0 ? resumen.horas_excedidas : resumen.horas_restantes)}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">H. Extras (horario)</div>
          <div className="text-2xl font-bold text-orange-600">{formatHoras(resumen.horas_extras_horario)}</div>
          <div className="text-xs text-gray-400 mt-1">Fuera de horario laboral</div>
        </div>
      </div>

      {/* Barra de progreso grande */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Utilización del período</span>
          <span className={`text-lg font-bold ${porcentaje > 100 ? 'text-red-600' : porcentaje > 80 ? 'text-yellow-600' : 'text-green-600'}`}>
            {porcentaje}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className={`h-4 rounded-full transition-all ${progressColor(porcentaje)}`}
            style={{ width: `${Math.min(100, porcentaje)}%` }}
          />
        </div>
        {porcentaje > 100 && (
          <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" />
            Cliente ha excedido sus horas contratadas en {formatHoras(resumen.horas_excedidas)}
          </div>
        )}
      </div>

      {/* Historial tendencia */}
      {historial && historial.historial && historial.historial.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Historial de Consumo
            </h3>
            <div className="flex items-center gap-2">
              <select value={histPeriodo} onChange={(e) => setHistPeriodo(e.target.value)}
                className="text-xs border rounded-md px-2 py-1">
                <option value="semana">Semanal</option>
                <option value="mes">Mensual</option>
              </select>
              <select value={histCantidad} onChange={(e) => setHistCantidad(Number(e.target.value))}
                className="text-xs border rounded-md px-2 py-1">
                <option value={6}>6 períodos</option>
                <option value={12}>12 períodos</option>
                <option value={24}>24 períodos</option>
              </select>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={historial.historial} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value, name) => {
                const labels = { horas_trabajadas: 'Trabajadas', horas_contratadas: 'Contratadas', horas_extras: 'Extras' };
                return [formatHoras(value), labels[name] || name];
              }} />
              <Legend formatter={(v) => {
                const labels = { horas_contratadas: 'Contratadas', horas_trabajadas: 'Trabajadas', horas_extras: 'Extras' };
                return labels[v] || v;
              }} />
              {historial.horas_contratadas_mes > 0 && (
                <Area type="monotone" dataKey="horas_contratadas" stroke="#94a3b8" fill="#f1f5f9" strokeDasharray="5 5" />
              )}
              <Area type="monotone" dataKey="horas_trabajadas" stroke="#3b82f6" fill="#dbeafe" strokeWidth={2} />
              <Area type="monotone" dataKey="horas_extras" stroke="#f97316" fill="#ffedd5" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Desglose por semana */}
      {semanas && semanas.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-emerald-600" />
              Desglose por Semana
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Semana</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Período</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Horas</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Extras</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Órdenes</th>
                </tr>
              </thead>
              <tbody>
                {semanas.map(s => (
                  <tr key={s.semana} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium">{s.semana}</td>
                    <td className="text-center px-3 py-2.5 text-gray-500 text-xs">{s.inicio} → {s.fin}</td>
                    <td className="text-center px-3 py-2.5 font-mono font-semibold">{formatHoras(s.horas)}</td>
                    <td className="text-center px-3 py-2.5 font-mono text-orange-600">
                      {s.extras_horario > 0 ? formatHoras(s.extras_horario) : '-'}
                    </td>
                    <td className="text-center px-3 py-2.5">{s.ordenes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Órdenes detalladas (expandible) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => setShowOrdenes(!showOrdenes)}
          className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Clock className="h-4 w-4 text-emerald-600" />
            Detalle de Órdenes ({ordenes.length})
          </h3>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${showOrdenes ? 'rotate-180' : ''}`} />
        </button>
        {showOrdenes && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Fecha</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Técnico</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Inicio</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Fin</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Duración</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Extras</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Descripción</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Ver</th>
                </tr>
              </thead>
              <tbody>
                {ordenes.map(o => (
                  <tr key={o.orden_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => onPreviewOrden(o.orden_id)}>
                    <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{o.fecha}</td>
                    <td className="px-3 py-2 text-gray-700">{o.nombre_tecnico || '-'}</td>
                    <td className="text-center px-3 py-2 font-mono text-xs">{formatTimeFromISO(o.hora_inicio)}</td>
                    <td className="text-center px-3 py-2 font-mono text-xs">{formatTimeFromISO(o.hora_fin)}</td>
                    <td className="text-center px-3 py-2 font-mono font-semibold" title={`Real: ${formatHoras(o.duracion_horas)}`}>
                      {formatHoras(o.duracion_computada ?? o.duracion_horas)}
                      {o.horas_extras_decimal > 0 && <span className="ml-1 text-xs text-orange-500">×2</span>}
                    </td>
                    <td className="text-center px-3 py-2 font-mono text-orange-600">
                      {o.horas_extras_decimal > 0 ? formatHoras(o.horas_extras_decimal) : '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs max-w-[280px] whitespace-normal break-words">{o.descripcion}</td>
                    <td className="text-center px-3 py-2">
                      <button className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors" title="Ver orden">
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}


// ============================================================
// SUB-COMPONENTE: DETALLE TÉCNICO
// ============================================================
function DetalleTecnicoView({ data, historial, formatHoras, histPeriodo, setHistPeriodo, histCantidad, setHistCantidad, onPreviewOrden }) {
  const { tecnico, resumen, semanas, por_cliente, ordenes } = data;
  const [showOrdenes, setShowOrdenes] = useState(false);

  return (
    <>
      {/* Resumen del técnico */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">Horas Computadas</div>
          <div className="text-2xl font-bold text-gray-900">{formatHoras(resumen.horas_computadas ?? resumen.horas_totales)}</div>
          <div className="text-xs text-gray-400 mt-1">{resumen.total_ordenes} órdenes</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">Horas Normales</div>
          <div className="text-2xl font-bold text-emerald-600">{formatHoras(resumen.horas_normales)}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">Horas Extras</div>
          <div className="text-2xl font-bold text-orange-600">{formatHoras(resumen.horas_extras)}</div>
          <div className="text-xs text-orange-400 mt-1">A cobrar</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 mb-1">Clientes Atendidos</div>
          <div className="text-2xl font-bold text-purple-600">{resumen.clientes_atendidos}</div>
        </div>
      </div>

      {/* Historial tendencia */}
      {historial && historial.historial && historial.historial.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              Historial de Horas
            </h3>
            <div className="flex items-center gap-2">
              <select value={histPeriodo} onChange={(e) => setHistPeriodo(e.target.value)}
                className="text-xs border rounded-md px-2 py-1">
                <option value="semana">Semanal</option>
                <option value="mes">Mensual</option>
              </select>
              <select value={histCantidad} onChange={(e) => setHistCantidad(Number(e.target.value))}
                className="text-xs border rounded-md px-2 py-1">
                <option value={6}>6 períodos</option>
                <option value={12}>12 períodos</option>
                <option value={24}>24 períodos</option>
              </select>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={historial.historial} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(value, name) => {
                const labels = { horas_trabajadas: 'Totales', horas_extras: 'Extras' };
                return [formatHoras(value), labels[name] || name];
              }} />
              <Legend formatter={(v) => {
                const labels = { horas_trabajadas: 'Totales', horas_extras: 'Extras' };
                return labels[v] || v;
              }} />
              <Bar dataKey="horas_trabajadas" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="horas_extras" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Desglose por cliente */}
      {por_cliente && por_cliente.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Building className="h-4 w-4 text-emerald-600" />
              Horas por Cliente
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Cliente</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Horas</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Extras</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Órdenes</th>
                </tr>
              </thead>
              <tbody>
                {por_cliente.map(pc => (
                  <tr key={pc.cliente_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-900">{pc.nombre_cliente || 'Sin cliente'}</td>
                    <td className="text-center px-3 py-2.5 font-mono font-semibold">{formatHoras(pc.horas)}</td>
                    <td className="text-center px-3 py-2.5 font-mono text-orange-600">
                      {pc.horas_extras > 0 ? formatHoras(pc.horas_extras) : '-'}
                    </td>
                    <td className="text-center px-3 py-2.5">{pc.ordenes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Desglose por semana */}
      {semanas && semanas.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-emerald-600" />
              Desglose por Semana
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Semana</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Período</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Totales</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Normales</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Extras</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Órdenes</th>
                </tr>
              </thead>
              <tbody>
                {semanas.map(s => (
                  <tr key={s.semana} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium">{s.semana}</td>
                    <td className="text-center px-3 py-2.5 text-gray-500 text-xs">{s.inicio} → {s.fin}</td>
                    <td className="text-center px-3 py-2.5 font-mono font-semibold">{formatHoras(s.horas_totales)}</td>
                    <td className="text-center px-3 py-2.5 font-mono text-emerald-600">{formatHoras(s.horas_normales)}</td>
                    <td className="text-center px-3 py-2.5 font-mono text-orange-600">
                      {s.horas_extras > 0 ? formatHoras(s.horas_extras) : '-'}
                    </td>
                    <td className="text-center px-3 py-2.5">{s.ordenes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Órdenes detalladas (expandible) */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <button
          onClick={() => setShowOrdenes(!showOrdenes)}
          className="w-full px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Clock className="h-4 w-4 text-emerald-600" />
            Detalle de Órdenes ({ordenes.length})
          </h3>
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${showOrdenes ? 'rotate-180' : ''}`} />
        </button>
        {showOrdenes && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Fecha</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Cliente</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Inicio</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Fin</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Duración</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Extras</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Descripción</th>
                  <th className="text-center px-3 py-2 font-medium text-gray-600">Ver</th>
                </tr>
              </thead>
              <tbody>
                {ordenes.map(o => (
                  <tr key={o.orden_id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => onPreviewOrden(o.orden_id)}>
                    <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{o.fecha}</td>
                    <td className="px-3 py-2 text-gray-700">{o.nombre_cliente || '-'}</td>
                    <td className="text-center px-3 py-2 font-mono text-xs">{formatTimeFromISO(o.hora_inicio)}</td>
                    <td className="text-center px-3 py-2 font-mono text-xs">{formatTimeFromISO(o.hora_fin)}</td>
                    <td className="text-center px-3 py-2 font-mono font-semibold" title={`Real: ${formatHoras(o.duracion_horas)}`}>
                      {formatHoras(o.duracion_computada ?? o.duracion_horas)}
                      {o.horas_extras_decimal > 0 && <span className="ml-1 text-xs text-orange-500">×2</span>}
                    </td>
                    <td className="text-center px-3 py-2 font-mono text-orange-600">
                      {o.horas_extras_decimal > 0 ? formatHoras(o.horas_extras_decimal) : '-'}
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs max-w-[280px] whitespace-normal break-words">{o.descripcion}</td>
                    <td className="text-center px-3 py-2">
                      <button className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors" title="Ver orden">
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}


// ============================================================
// HELPERS
// ============================================================
function KpiCard({ icon, label, value, color }) {
  const bgColors = {
    blue: 'bg-emerald-50 border-emerald-100',
    green: 'bg-green-50 border-green-100',
    purple: 'bg-purple-50 border-purple-100',
    red: 'bg-red-50 border-red-100',
    yellow: 'bg-yellow-50 border-yellow-100',
    orange: 'bg-orange-50 border-orange-100',
  };

  return (
    <div className={`rounded-xl border p-3 sm:p-4 ${bgColors[color] || bgColors.blue}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-xs text-gray-600">{label}</span>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-gray-900">{value}</div>
    </div>
  );
}

function formatTimeFromISO(val) {
  if (!val) return '-';
  try {
    const s = String(val);
    if (s.includes('T')) {
      const d = new Date(s);
      return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    }
    if (s.includes(':')) return s.substring(0, 5);
    return s;
  } catch {
    return val;
  }
}


// ============================================================
// MODAL: PREVIEW DE ORDEN
// ============================================================
function OrdenPreviewModal({ orden, loading, onClose, formatHoras }) {
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-white rounded-xl p-8 flex items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          <span className="text-gray-600">Cargando orden...</span>
        </div>
      </div>
    );
  }

  if (!orden) return null;

  const fecha = orden.fecha_iso || orden.fecha || '';
  const fechaDisplay = fecha ? fecha.substring(0, 10) : '';
  const hiDisplay = formatTimeFromISO(orden.hora_inicio_iso || orden.hora_inicio);
  const hfDisplay = formatTimeFromISO(orden.hora_fin_iso || orden.hora_fin);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Orden #{orden.numero_orden || orden._id?.slice(-6)}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                orden.estado === 'completada' ? 'bg-green-100 text-green-700' :
                orden.estado === 'en_proceso' ? 'bg-emerald-100 text-emerald-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {orden.estado || 'borrador'}
              </span>
              {orden.tipo_servicio && (
                <span className="text-xs text-gray-500">{orden.tipo_servicio}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => descargarPdfOrden(orden._id || orden.id, orden.numero_orden)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              title="Descargar PDF"
            >
              <Download className="h-4 w-4" />
              PDF
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Info principal */}
          <div className="grid grid-cols-2 gap-4">
            <InfoField label="Cliente" value={orden.nombre_cliente} />
            <InfoField label="Fecha" value={fechaDisplay} />
            <InfoField label="Técnico" value={orden.nombre_tecnico || orden.tecnico_nombre || orden.tecnico_asignado_nombre || '-'} />
            <InfoField label="Sucursal" value={orden.sucursal || '-'} />
          </div>

          {/* Horarios */}
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
            <h3 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Horarios y Duración
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-emerald-600">Inicio</span>
                <div className="text-lg font-mono font-bold text-emerald-900">{hiDisplay}</div>
              </div>
              <div>
                <span className="text-xs text-emerald-600">Fin</span>
                <div className="text-lg font-mono font-bold text-emerald-900">{hfDisplay}</div>
              </div>
              <div>
                <span className="text-xs text-emerald-600">Duración</span>
                <div className="text-lg font-mono font-bold text-emerald-900">
                  {orden.duracion_formato || orden.duracion_decimal ? `${orden.duracion_decimal}h` : '-'}
                </div>
              </div>
            </div>
          </div>

          {/* Dirección */}
          {orden.direccion_cliente && (
            <InfoField label="Dirección" value={orden.direccion_cliente} />
          )}

          {/* Equipos */}
          {orden.equipos && orden.equipos.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Equipos ({orden.equipos.length})</h3>
              <div className="space-y-2">
                {orden.equipos.map((eq, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="font-medium text-gray-900 text-sm">{eq.nombre || eq.descripcion || 'Equipo'}</div>
                    <div className="text-xs text-gray-500 mt-0.5 flex gap-3 flex-wrap">
                      {eq.marca && <span>Marca: {eq.marca}</span>}
                      {eq.modelo && <span>Modelo: {eq.modelo}</span>}
                      {eq.numero_serie && <span>Serie: {eq.numero_serie}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Trabajos realizados */}
          {orden.trabajos_realizados && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Trabajos Realizados</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-200">
                {orden.trabajos_realizados}
              </p>
            </div>
          )}

          {/* Observaciones */}
          {orden.observaciones && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">Observaciones</h3>
              <p className="text-sm text-gray-600 whitespace-pre-wrap bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                {orden.observaciones}
              </p>
            </div>
          )}

          {/* Firma */}
          {orden.firma_cliente && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Firma del Cliente</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-3 inline-block">
                <img
                  src={orden.firma_cliente.startsWith('data:') ? orden.firma_cliente : `data:image/png;base64,${orden.firma_cliente}`}
                  alt="Firma"
                  className="max-h-24"
                />
              </div>
              {(orden.aclaracion_firma || orden.aclaracion_firma_cliente || orden.firma_aclaracion) && (
                <p className="text-sm text-gray-500 mt-1">
                  Aclaración: {orden.aclaracion_firma || orden.aclaracion_firma_cliente || orden.firma_aclaracion}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <span className="text-xs text-gray-500">{label}</span>
      <div className="text-sm font-medium text-gray-900">{value || '-'}</div>
    </div>
  );
}


// ============================================================
// MODAL: EXPORTAR DATOS
// ============================================================
function ExportModal({ onClose, periodo, anio, valor, vista, selectedId, selectedNombre }) {
  const MESES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

  const [formato, setFormato] = useState('excel');
  const [tipoExport, setTipoExport] = useState(() => {
    if (vista === 'detalle_cliente') return 'detalle_cliente';
    if (vista === 'detalle_tecnico') return 'detalle_tecnico';
    if (vista === 'tecnicos') return 'tecnicos';
    return 'clientes';
  });
  const [exportPeriodo, setExportPeriodo] = useState(periodo);
  const now = new Date();
  const [exportAnio, setExportAnio] = useState(anio);
  const [exportValor, setExportValor] = useState(valor);
  const [incluir, setIncluir] = useState({
    resumen: true,
    detalle_ordenes: true,
    desglose_semanal: true,
    desglose_clientes: true,
  });
  const [seleccionIds, setSeleccionIds] = useState(() => {
    if (selectedId) return [selectedId];
    return [];
  });
  const [disponibles, setDisponibles] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [searchExp, setSearchExp] = useState('');

  // Load available clients or technicians
  useEffect(() => {
    const cargar = async () => {
      setLoadingList(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      try {
        if (tipoExport === 'clientes' || tipoExport === 'detalle_cliente') {
          const res = await http.get(`/control-horas/resumen-clientes?periodo=${exportPeriodo}&anio=${exportAnio}&valor=${exportValor}`, { headers });
          setDisponibles(res.data.clientes.map(c => ({ id: c.cliente_id, nombre: c.nombre, extra: `${c.ordenes_count} órdenes` })));
        } else {
          const res = await http.get(`/control-horas/resumen-tecnicos?periodo=${exportPeriodo}&anio=${exportAnio}&valor=${exportValor}`, { headers });
          setDisponibles(res.data.tecnicos.map(t => ({ id: t.tecnico_id, nombre: t.nombre, extra: `${t.ordenes_count} órdenes` })));
        }
      } catch (err) {
        console.error('Error cargando lista:', err);
      }
      setLoadingList(false);
    };
    cargar();
  }, [tipoExport, exportPeriodo, exportAnio, exportValor]);

  const toggleId = (id) => {
    setSeleccionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const seleccionarTodos = () => {
    if (seleccionIds.length === disponibles.length) {
      setSeleccionIds([]);
    } else {
      setSeleccionIds(disponibles.map(d => d.id));
    }
  };

  const periodoLabel = () => {
    if (exportPeriodo === 'mes') return `${MESES[exportValor]} ${exportAnio}`;
    if (exportPeriodo === 'semana') return `Semana ${exportValor}, ${exportAnio}`;
    return `${exportAnio}`;
  };

  const handleExport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        formato,
        tipo: tipoExport,
        ids: seleccionIds,
        periodo: exportPeriodo,
        anio: exportAnio,
        valor: exportValor,
        incluir,
      };

      const res = await http.post('/control-horas/exportar', payload, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000,
      });

      const ext = formato === 'excel' ? 'xlsx' : 'pdf';
      const mime = formato === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `control_horas_${periodoLabel().replace(/\s+/g, '_').replace(',', '')}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      onClose();
    } catch (err) {
      console.error('Error exportando:', err);
      alert('Error al exportar. Intente de nuevo.');
    }
    setDownloading(false);
  };

  const filtrados = disponibles.filter(d =>
    d.nombre.toLowerCase().includes(searchExp.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex items-center justify-between z-10">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Download className="h-5 w-5 text-emerald-600" />
            Exportar Control de Horas
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Formato */}
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Formato de descarga</label>
            <div className="flex gap-3">
              <button
                onClick={() => setFormato('excel')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                  formato === 'excel'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <FileSpreadsheet className="h-5 w-5" />
                <span className="font-medium">Excel</span>
              </button>
              <button
                onClick={() => setFormato('pdf')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                  formato === 'pdf'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <FileText className="h-5 w-5" />
                <span className="font-medium">PDF</span>
              </button>
            </div>
          </div>

          {/* Tipo de reporte */}
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Tipo de reporte</label>
            <select
              value={tipoExport}
              onChange={(e) => { setTipoExport(e.target.value); setSeleccionIds([]); }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
            >
              <option value="clientes">Resumen por Clientes</option>
              <option value="detalle_cliente">Detalle de Clientes (con órdenes)</option>
              <option value="tecnicos">Resumen por Técnicos</option>
              <option value="detalle_tecnico">Detalle de Técnicos (con órdenes)</option>
            </select>
          </div>

          {/* Período */}
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Período</label>
            <div className="flex gap-2">
              <select
                value={exportPeriodo}
                onChange={(e) => {
                  setExportPeriodo(e.target.value);
                  const n = new Date();
                  if (e.target.value === 'mes') setExportValor(n.getMonth() + 1);
                  else if (e.target.value === 'semana') {
                    const d2 = new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate()));
                    d2.setUTCDate(d2.getUTCDate() + 4 - (d2.getUTCDay() || 7));
                    const ys = new Date(Date.UTC(d2.getUTCFullYear(), 0, 1));
                    setExportValor(Math.ceil((((d2 - ys) / 86400000) + 1) / 7));
                  } else setExportValor(n.getFullYear());
                  setExportAnio(n.getFullYear());
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
              >
                <option value="semana">Semanal</option>
                <option value="mes">Mensual</option>
                <option value="anio">Anual</option>
              </select>
              {exportPeriodo === 'mes' && (
                <select value={exportValor} onChange={(e) => setExportValor(Number(e.target.value))}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500">
                  {MESES.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                </select>
              )}
              {exportPeriodo === 'semana' && (
                <input type="number" min={1} max={53} value={exportValor}
                  onChange={(e) => setExportValor(Number(e.target.value))}
                  className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                  placeholder="Semana"
                />
              )}
              <input type="number" min={2020} max={2030} value={exportAnio}
                onChange={(e) => setExportAnio(Number(e.target.value))}
                className="w-24 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500"
                placeholder="Año"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Período seleccionado: <strong>{periodoLabel()}</strong></p>
          </div>

          {/* Selección de clientes/técnicos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">
                {tipoExport.includes('cliente') ? 'Clientes' : 'Técnicos'} a incluir
              </label>
              <button onClick={seleccionarTodos} className="text-xs text-emerald-600 hover:text-emerald-800">
                {seleccionIds.length === disponibles.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </button>
            </div>

            {seleccionIds.length === 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200 mb-2">
                Sin selección = se exportan todos
              </p>
            )}

            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar..."
                value={searchExp}
                onChange={(e) => setSearchExp(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm"
              />
            </div>

            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {loadingList ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-emerald-600 mr-2" />
                  <span className="text-sm text-gray-500">Cargando...</span>
                </div>
              ) : filtrados.length === 0 ? (
                <div className="text-center py-4 text-sm text-gray-500">Sin resultados</div>
              ) : filtrados.map(d => (
                <label key={d.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={seleccionIds.includes(d.id)}
                    onChange={() => toggleId(d.id)}
                    className="h-4 w-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900 truncate block">{d.nombre}</span>
                    <span className="text-xs text-gray-500">{d.extra}</span>
                  </div>
                  {seleccionIds.includes(d.id) && <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />}
                </label>
              ))}
            </div>
            {seleccionIds.length > 0 && (
              <p className="text-xs text-emerald-600 mt-1">{seleccionIds.length} seleccionado(s)</p>
            )}
          </div>

          {/* Secciones a incluir */}
          <div>
            <label className="text-sm font-semibold text-gray-700 block mb-2">Secciones a incluir</label>
            <div className="space-y-2">
              {[
                { key: 'resumen', label: 'Resumen general', desc: 'Horas contratadas, trabajadas, extras' },
                { key: 'detalle_ordenes', label: 'Detalle de órdenes', desc: 'Lista completa de cada orden con horarios' },
                { key: 'desglose_semanal', label: 'Desglose semanal', desc: 'Horas agrupadas por semana' },
                ...(tipoExport.includes('tecnico') ? [{ key: 'desglose_clientes', label: 'Desglose por cliente', desc: 'Horas por cada cliente atendido' }] : []),
              ].map(s => (
                <label key={s.key} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={incluir[s.key]}
                    onChange={() => setIncluir(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
                    className="h-4 w-4 mt-0.5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">{s.label}</span>
                    <span className="text-xs text-gray-500 block">{s.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-between">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors">
            Cancelar
          </button>
          <button
            onClick={handleExport}
            disabled={downloading}
            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Descargar {formato === 'excel' ? 'Excel' : 'PDF'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
